// Collection product pinning via URL parameter: ?Sort_by=handle or ?Sort_by=handle1,handle2
(function () {
  const PIN_PARAM = 'Sort_by';
  const PINNED_ATTR = 'data-pinned-product';
  const PRODUCT_GRID_SELECTOR = 'ul#product-grid';
  const PRODUCT_ITEM_SELECTOR = 'li.grid__item';
  let sharedInstance = null;

  class CollectionSortByPin {
    constructor(api) {
      this.api = api || null;
      this.isApplying = false;
      this.applyTimer = null;
      this.observer = null;
      this.lastSignature = '';
    }

    setApi(api) {
      if (api) this.api = api;
    }

    start() {
      this.syncUrlHelpers();
      this.observeGridUpdates();
      this.scheduleApply();
    }

    getGrid() {
      return document.querySelector(PRODUCT_GRID_SELECTOR);
    }

    getCollectionHandle() {
      const gridHandle = this.getGrid()?.dataset.collectionHandle;
      if (gridHandle) return gridHandle;

      const match = window.location.pathname.match(/\/collections\/([^/]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    }

    getPinValues() {
      const rawValue = new URLSearchParams(window.location.search).get(PIN_PARAM);
      if (!rawValue) return [];

      return rawValue
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }

    getPinParamValue() {
      return new URLSearchParams(window.location.search).get(PIN_PARAM) || '';
    }

    syncUrlHelpers() {
      this.syncFacetInputs();
      this.syncPaginationLinks();
    }

    syncFacetInputs() {
      const pinValue = this.getPinParamValue();
      const forms = Array.from(document.querySelectorAll('facet-filters-form form'));
      const targetForms = forms.filter((form) => form.id === 'FacetFiltersForm' || form.id === 'FacetFiltersFormMobile');
      if (targetForms.length === 0) {
        targetForms.push(...forms);
      }

      forms.forEach((form) => {
        let input = form.querySelector(`input[type="hidden"][name="${PIN_PARAM}"]`);
        const shouldKeepInput = targetForms.includes(form);

        if (!pinValue || !shouldKeepInput) {
          input?.remove();
          return;
        }

        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = PIN_PARAM;
          form.appendChild(input);
        }

        input.value = pinValue;
      });
    }

    syncPaginationLinks() {
      const pinValue = this.getPinParamValue();
      if (!pinValue) return;

      document.querySelectorAll('.pagination a[href]').forEach((link) => {
        try {
          const url = new URL(link.getAttribute('href'), window.location.origin);
          url.searchParams.set(PIN_PARAM, pinValue);
          link.href = `${url.pathname}${url.search}${url.hash}`;
        } catch (error) {
          console.warn('Unable to preserve pinned products in pagination link:', error);
        }
      });
    }

    observeGridUpdates() {
      const container = document.getElementById('ProductGridContainer');
      if (!container || this.observer) return;

      this.observer = new MutationObserver(() => {
        if (this.isApplying) return;
        this.syncUrlHelpers();
        this.scheduleApply();
      });

      this.observer.observe(container, { childList: true, subtree: true });
    }

    scheduleApply() {
      window.clearTimeout(this.applyTimer);
      this.applyTimer = window.setTimeout(() => this.apply(), 50);
    }

    async apply() {
      const values = this.getPinValues();
      if (values.length === 0) return;

      const grid = this.getGrid();
      if (!grid) return;

      const signature = `${window.location.pathname}?${window.location.search}|${values.join(',')}|${grid.children.length}`;
      if (this.isApplying && this.lastSignature === signature) return;

      this.isApplying = true;
      this.lastSignature = signature;

      try {
        const elements = [];
        const seenHandles = new Set();

        for (const value of values) {
          const handle = await this.resolveHandle(value);
          if (!handle || seenHandles.has(handle)) continue;

          seenHandles.add(handle);

          const existing = this.findExistingProductElement(grid, handle);
          const element = existing || (await this.fetchProductItemElement(handle));

          if (!element) {
            console.warn(`Pinned product could not be rendered: ${value}`);
            continue;
          }

          this.removeDuplicateProductElements(grid, handle, element);
          element.setAttribute(PINNED_ATTR, 'true');
          element.dataset.productHandle = handle;
          elements.push(element);
        }

        if (elements.length === 0) return;

        const fragment = document.createDocumentFragment();
        elements.forEach((element) => fragment.appendChild(element));
        grid.insertBefore(fragment, grid.firstChild);
        this.reinitializePinnedItems(elements);
        this.syncUrlHelpers();
      } finally {
        window.setTimeout(() => {
          this.isApplying = false;
        }, 0);
      }
    }

    async resolveHandle(value) {
      if (!CollectionSortByPin.isNumericId(value)) return value;

      if (!this.api || typeof this.api.shopifyFetch !== 'function') {
        console.warn('ShopifyStorefrontAPI is unavailable; numeric pinned product IDs cannot be resolved.');
        return null;
      }

      const query = `
        query ($id: ID!) {
          product(id: $id) {
            handle
          }
        }
      `;

      try {
        const result = await this.api.shopifyFetch(query, { id: `gid://shopify/Product/${value}` }, { useCache: true });
        return result?.product?.handle || null;
      } catch (error) {
        console.error(`Failed to resolve pinned product ID ${value}:`, error);
        return null;
      }
    }

    findExistingProductElement(grid, handle) {
      return Array.from(grid.querySelectorAll(PRODUCT_ITEM_SELECTOR)).find((item) => item.dataset.productHandle === handle);
    }

    removeDuplicateProductElements(grid, handle, keepElement) {
      Array.from(grid.querySelectorAll(PRODUCT_ITEM_SELECTOR)).forEach((item) => {
        if (item !== keepElement && item.dataset.productHandle === handle) {
          item.remove();
        }
      });
    }

    async fetchProductItemElement(handle) {
      const collectionHandle = this.getCollectionHandle();
      const productPaths = [];

      if (collectionHandle) {
        productPaths.push(`/collections/${encodeURIComponent(collectionHandle)}/products/${encodeURIComponent(handle)}`);
      }

      productPaths.push(`/products/${encodeURIComponent(handle)}`);

      try {
        let response = null;

        for (const productPath of productPaths) {
          response = await fetch(`${productPath}?section_id=product-card`, {
            credentials: 'same-origin',
          });

          if (response.ok) break;
        }

        if (!response || !response.ok) {
          console.warn(`Failed to fetch pinned product card: ${handle}`, response?.status);
          return null;
        }

        const html = await response.text();
        const temp = document.createElement('div');
        temp.innerHTML = html;

        const productElement = temp.querySelector(`${PRODUCT_ITEM_SELECTOR}[data-product-handle]`) || temp.querySelector(PRODUCT_ITEM_SELECTOR);
        if (!productElement) {
          console.warn(`Pinned product card section did not return a grid item: ${handle}`);
          return null;
        }

        productElement.dataset.productHandle = handle;
        return productElement;
      } catch (error) {
        console.error(`Failed to fetch pinned product card: ${handle}`, error);
        return null;
      }
    }

    reinitializePinnedItems(elements) {
      elements.forEach((element) => {
        if (window.customElements?.upgrade) {
          window.customElements.upgrade(element);
        }
      });

      document.dispatchEvent(
        new CustomEvent('collection:pinned-products-rendered', {
          detail: { elements },
        })
      );

      if (window.Loox?.refreshWidgets) {
        window.Loox.refreshWidgets();
      }
    }

    static isNumericId(value) {
      return /^\d+$/.test(String(value));
    }
  }

  function startCollectionSortByPin(api) {
    if (!sharedInstance) {
      sharedInstance = new CollectionSortByPin(api);
      sharedInstance.start();
    } else {
      sharedInstance.setApi(api);
      sharedInstance.start();
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    startCollectionSortByPin(window.ShopifyStorefrontAPI);

    if (window.ShopifyStorefrontAPI?.onInit) {
      window.ShopifyStorefrontAPI.onInit(() => startCollectionSortByPin(window.ShopifyStorefrontAPI));
    } else {
      document.addEventListener('ShopifyStorefrontAPI:init', (event) => {
        startCollectionSortByPin(event?.detail?.api || window.ShopifyStorefrontAPI);
      });
    }
  });

  window.addEventListener('popstate', () => {
    if (!sharedInstance) return;
    sharedInstance.syncUrlHelpers();
    sharedInstance.scheduleApply();
  });
})();
